import { Component, EventEmitter, Input, Output, OnInit, OnChanges, OnDestroy, SimpleChanges, ElementRef, HostListener } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface TreeNode {
  id: string;
  name: string;
  fullPath: string;
  level: number;
  expanded: boolean;
  visible: boolean;
  children?: TreeNode[];
  parent?: TreeNode;
}

@Component({
  selector: 'digi-tree-select',
  templateUrl: './tree-select.component.html',
  styleUrls: ['./tree-select.component.scss']
})
export class TreeSelectComponent implements OnInit, OnChanges, OnDestroy {
  @Input() label: string = 'Chọn thư mục';
  @Input() options: any[] = [];
  @Input() control: FormControl = new FormControl('');
  @Input() disabled: boolean = false;
  @Input() placeholder: string = '';
  @Input() errorMessage: string = '';
  @Input() isSubmit: boolean = false;
  @Input() loading: boolean = false;
  @Output() selectionChange = new EventEmitter<any>();

  searchText: string = '';
  treeNodes: TreeNode[] = [];
  filteredNodes: TreeNode[] = [];
  isDropdownOpen: boolean = false;
  selectedNode: TreeNode | null = null;
  isLoading: boolean = false;
  private destroy$ = new Subject<void>();

  constructor(private elementRef: ElementRef) {}

  ngOnInit() {
    this.buildTree();
    this.control.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.selectionChange.emit(value);
        this.updateSelectedNodeFromValue(value);
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['options'] && changes['options'].currentValue) {
      console.log('Options changed, rebuilding tree:', changes['options'].currentValue);
      this.buildTree();
    }
  }

  buildTree() {
    console.log('Building tree with options:', this.options);
    this.isLoading = this.loading;
    
    if (this.isLoading) {
      this.treeNodes = [];
      this.filteredNodes = [];
      return;
    }
    
    // Check if options already have tree structure (with children property)
    if (this.options && this.options.length > 0 && this.options[0].children !== undefined) {
      this.treeNodes = this.convertExistingTreeToNodes(this.options);
    } else {
      this.treeNodes = this.convertToTreeNodes(this.options);
    }
    this.filteredNodes = [...this.treeNodes];
    console.log('Built tree nodes:', this.treeNodes);
  }

  convertExistingTreeToNodes(options: any[]): TreeNode[] {
    return options.map(option => this.convertSingleNode(option));
  }

  convertSingleNode(option: any): TreeNode {
    const node: TreeNode = {
      id: option.id || option.FullPath,
      name: option.name || option.DisplayName,
      fullPath: option.fullPath || option.FullPath,
      level: option.level || 0,
      expanded: option.expanded || false,
      visible: option.visible !== false,
      children: []
    };

    if (option.children && option.children.length > 0) {
      node.children = option.children.map((child: any) => this.convertSingleNode(child));
    }

    return node;
  }

  convertToTreeNodes(options: any[]): TreeNode[] {
    const nodes: TreeNode[] = [];
    const nodeMap = new Map<string, TreeNode>();

    // First pass: create all nodes
    options.forEach(option => {
      const node: TreeNode = {
        id: option.FullPath || option.id,
        name: option.DisplayName || option.name,
        fullPath: option.FullPath || option.fullPath,
        level: this.calculateLevel(option.DisplayName || option.name),
        expanded: false,
        visible: true,
        children: []
      };
      nodeMap.set(node.id, node);
    });

    // Second pass: build hierarchy
    options.forEach(option => {
      const node = nodeMap.get(option.FullPath || option.id);
      if (node) {
        const parentPath = this.getParentPath(node.fullPath);
        if (parentPath && nodeMap.has(parentPath)) {
          const parent = nodeMap.get(parentPath)!;
          parent.children!.push(node);
          node.parent = parent;
        } else {
          nodes.push(node);
        }
      }
    });

    return nodes;
  }

  calculateLevel(displayName: string): number {
    // Count leading non-breaking spaces to determine level
    const matches = displayName.match(/^\u00A0+/);
    return matches ? matches[0].length / 4 : 0; // 4 spaces per level
  }

  getParentPath(fullPath: string): string | null {
    const parts = fullPath.split('\\');
    if (parts.length <= 1) return null;
    parts.pop();
    return parts.join('\\');
  }

  toggleNode(node: TreeNode) {
    if (node.children && node.children.length > 0) {
      node.expanded = !node.expanded;
      this.updateNodeVisibility();
    }
    // Remove auto-selection of leaf nodes - now user must click "Chọn" button
  }

  selectNode(node: TreeNode) {
    this.selectedNode = node;
    this.control.setValue(node.fullPath);
    this.isDropdownOpen = false;
    this.selectionChange.emit(node.fullPath);
    console.log('Selected folder:', node.fullPath);
  }

  onSearchInput() {
    this.filterTree();
    this.isDropdownOpen = true;
  }

  filterTree() {
    if (!this.searchText.trim()) {
      this.filteredNodes = [...this.treeNodes];
      this.resetAllExpanded();
      return;
    }

    const searchLower = this.searchText.toLowerCase();
    this.filteredNodes = this.filterNodesRecursive(this.treeNodes, searchLower);
    this.expandAllMatchingNodes();
  }

  filterNodesRecursive(nodes: TreeNode[], searchText: string): TreeNode[] {
    const filtered: TreeNode[] = [];

    nodes.forEach(node => {
      const matches = node.name.toLowerCase().includes(searchText);
      const filteredChildren = node.children ? this.filterNodesRecursive(node.children, searchText) : [];

      if (matches || filteredChildren.length > 0) {
        const filteredNode = { ...node };
        filteredNode.children = filteredChildren;
        filteredNode.expanded = filteredChildren.length > 0;
        filteredNode.visible = true;
        filtered.push(filteredNode);
      }
    });

    return filtered;
  }

  expandAllMatchingNodes() {
    const expandRecursive = (nodes: TreeNode[]) => {
      nodes.forEach(node => {
        node.expanded = true;
        if (node.children) {
          expandRecursive(node.children);
        }
      });
    };
    expandRecursive(this.filteredNodes);
  }

  resetAllExpanded() {
    const resetRecursive = (nodes: TreeNode[]) => {
      nodes.forEach(node => {
        node.expanded = false;
        if (node.children) {
          resetRecursive(node.children);
        }
      });
    };
    resetRecursive(this.filteredNodes);
  }

  updateNodeVisibility() {
    // This method can be used to update visibility based on expanded state
    // Currently handled in the template
  }

  clearSearch() {
    this.searchText = '';
    this.filterTree();
  }

  toggleDropdown() {
    this.isDropdownOpen = !this.isDropdownOpen;
    console.log('Dropdown toggled:', this.isDropdownOpen);
    console.log('Available nodes:', this.filteredNodes);
  }

  getSelectedDisplayName(): string {
    if (this.selectedNode) {
      //return this.cleanNodeName(this.selectedNode.name);
      return this.selectedNode.fullPath || this.cleanNodeName(this.selectedNode.name);
    }
    return this.control.value || '';
  }

  getNodeIndent(node: TreeNode): string {
    return 'margin-left: ' + (node.level * 20) + 'px';
  }

  trackByNodeId(index: number, node: TreeNode): string {
    return node.id;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isDropdownOpen = false;
    }
  }

  cleanNodeName(name: string): string {
    return name.replace(/^\u00A0+/, '');
  }

  updateSelectedNodeFromValue(value: string) {
    if (!value) {
      this.selectedNode = null;
      return;
    }

    // Find the node with matching fullPath
    const findNode = (nodes: TreeNode[]): TreeNode | null => {
      for (const node of nodes) {
        if (node.fullPath === value) {
          return node;
        }
        if (node.children) {
          const found = findNode(node.children);
          if (found) return found;
        }
      }
      return null;
    };

    this.selectedNode = findNode(this.treeNodes);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
